import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import api from '../lib/api';

interface AggSource {
  id: string;
  targetQuestionnaireFieldId: string;
  sourceType: 'QUESTIONNAIRE' | 'API';
  sourceQuestionnaireId?: string;
  sourceFieldId?: string;
  sourceApiEndpointId?: string;
  aggregationMode: string;
  timer: string;
  includedOrgIds?: string;
  targetQuestionnaireField: {
    dataField: { id: string; name: string; label: string | null };
  };
  sourceField?: { id: string; name: string; label: string | null };
  sourceQuestionnaire?: { id: string; name: string };
  sourceApiEndpoint?: { id: string; name: string };
}

interface FieldItem {
  id: string;
  dataFieldId: string;
  dataField: { id: string; name: string; label: string | null; type: string };
  label?: string;
  sortOrder: number;
}

const MODE_L: Record<string, string> = {
  SUM: 'Summe', AVERAGE: 'Durchschnitt', COUNT: 'Anzahl',
  OVERWRITE: 'Überschreiben', LAST_VALUE: 'Letzter Wert',
};
const TIMER_L: Record<string, string> = {
  ON_SUBMIT: 'Bei jeder Einreichung', HOURLY: 'Stündlich',
  EVERY_6H: 'Alle 6 Stunden', DAILY: 'Einmal täglich (00:00)',
};

export default function DataSourcesTab({
  questionnaireId, userOrgId, fields,
}: {
  questionnaireId: string; userOrgId: string; fields: FieldItem[];
}) {
  const orgScope = userOrgId; // When called from FillQuestionnairePage, sources are scoped to user's org
  const qc = useQueryClient();

  const { data: sources = [], isLoading: srcLoad } = useQuery<AggSource[]>({
    queryKey: ['agg-src', questionnaireId, orgScope],
    queryFn: async () => {
      const url = '/aggregation/sources/' + questionnaireId + (orgScope ? '?orgId=' + encodeURIComponent(orgScope) : '');
      const r = await api.get(url);
      return r.data;
    },
  });

  const { data: availQ = [] } = useQuery<any[]>({
    queryKey: ['avail-q', userOrgId, questionnaireId],
    queryFn: async () => {
      const r = await api.get('/aggregation/available-questionnaires/' + userOrgId + '?targetQuestionnaireId=' + questionnaireId);
      // Ensure each questionnaire has its fields with types for datatype matching
      return (r.data || []).map((q: any) => ({
        ...q,
        fields: q.fields || [],
      }));
    },
    enabled: !!userOrgId,
  });

  const { data: availApis = [] } = useQuery<any[]>({
    queryKey: ['avail-api', userOrgId],
    queryFn: async () => { const r = await api.get('/aggregation/available-apis/' + userOrgId); return r.data; },
    enabled: !!userOrgId,
  });

  const [editingField, setEditingField] = useState<string | null>(null);
  const [form, setForm] = useState({
    sourceType: 'QUESTIONNAIRE' as 'QUESTIONNAIRE' | 'API',
    sourceQuestionnaireId: '',
    sourceFieldId: '',
    sourceApiEndpointId: '',
    apiFieldId: '',
    aggregationMode: 'SUM',
    timer: 'ON_SUBMIT',
    includedOrgIds: '',
  });

  // Build a map of target field types for datatype validation
  const targetFieldTypes = new Map<string, string>();
  fields?.forEach((f: any) => {
    targetFieldTypes.set(f.dataFieldId, f.dataField?.type || '');
    targetFieldTypes.set(f.id, f.dataField?.type || '');
  });

  // Get the currently selected target field's type
  const editingTargetType = editingField ? targetFieldTypes.get(editingField) || targetFieldTypes.get(fields.find((f: any) => f.id === editingField)?.dataFieldId || '') : '';

  const { data: childOrgs = [] } = useQuery<any[]>({
    queryKey: ['child-org', userOrgId],
    queryFn: async () => { const r = await api.get('/aggregation/child-orgs/' + userOrgId); return r.data; },
    enabled: !!userOrgId,
  });

  const createM = useMutation({
    mutationFn: async (d: any) => { const r = await api.post('/aggregation/sources', d); return r.data; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agg-src'] }); },
  });
  const deleteM = useMutation({
    mutationFn: async (id: string) => { await api.delete('/aggregation/sources/' + id); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['agg-src'] }); },
  });

  function getSource(fieldId: string): AggSource | undefined {
    return (sources as AggSource[]).find((s) => s.targetQuestionnaireFieldId === fieldId);
  }

  function openEdit(fieldId: string) {
    const s = getSource(fieldId);
    if (s) {
      setForm({
        sourceType: s.sourceType,
        sourceQuestionnaireId: s.sourceQuestionnaireId || '',
        sourceFieldId: s.sourceFieldId || '',
        sourceApiEndpointId: s.sourceApiEndpointId || '',
        apiFieldId: '',
        aggregationMode: s.aggregationMode,
        timer: s.timer,
        includedOrgIds: s.includedOrgIds || '',
      });
    } else {
      setForm({
        sourceType: 'QUESTIONNAIRE',
        sourceQuestionnaireId: '',
        sourceFieldId: '',
        sourceApiEndpointId: '',
        apiFieldId: '',
        aggregationMode: 'SUM',
        timer: 'ON_SUBMIT',
        includedOrgIds: '',
      });
    }
    setEditingField(fieldId);
  }

  function save(fieldId: string) {
    const payload: any = {
      targetQuestionnaireFieldId: fieldId,
      sourceType: form.sourceType,
      aggregationMode: form.aggregationMode,
      timer: form.timer,
      includedOrgIds: form.includedOrgIds || null,
      organizationId: orgScope || undefined,
    };
    if (form.sourceType === 'QUESTIONNAIRE') {
      payload.sourceQuestionnaireId = form.sourceQuestionnaireId;
      payload.sourceFieldId = form.sourceFieldId;
    } else {
      payload.sourceApiEndpointId = form.sourceApiEndpointId;
      payload.sourceFieldId = form.apiFieldId;
    }

    const existing = getSource(fieldId);
    if (existing) {
      api.put('/aggregation/sources/' + existing.id, payload).then(() => {
        qc.invalidateQueries({ queryKey: ['agg-src'] });
        setEditingField(null);
      });
    } else {
      api.post('/aggregation/sources', payload).then(() => {
        qc.invalidateQueries({ queryKey: ['agg-src'] });
        setEditingField(null);
      });
    }
  }

  function removeSource(sourceId: string) {
    if (window.confirm('Datenquelle wirklich löschen?')) {
      deleteM.mutate(sourceId);
    }
  }

  const pend = createM.isPending || deleteM.isPending;
  if (srcLoad) return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-400" size={24} /></div>;

  const fieldMap = new Map<string, FieldItem>();
  fields?.forEach((f: FieldItem) => fieldMap.set(f.id, f));

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Wahle fur jedes Feld aus, woher die Daten kommen sollen (Quell-Fragebogen oder API).
      </p>

      {fields?.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-400">
          Dieser Fragebogen hat noch keine Felder. Fuge zuerst Felder hinzu.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Feld</th>
                <th className="px-4 py-3 text-left font-medium text-gray-600">Quelle</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Aggregation</th>
                <th className="px-4 py-3 text-center font-medium text-gray-600">Timer</th>
                <th className="px-4 py-3 text-right font-medium text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {fields.map((f: FieldItem) => {
                const src = getSource(f.id);
                const fieldLabel = f.label || f.dataField?.label || f.dataField?.name || '---';
                return (
                  <tr key={f.id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{fieldLabel}</td>
                    <td className="px-4 py-3">
                      {src ? (
                        <span className="flex items-center gap-1 text-gray-700">
                          {src.sourceType === 'QUESTIONNAIRE'
                            ? (<>{'📋'} {src.sourceQuestionnaire?.name || '?'} {'\u2192'} {src.sourceField?.name || '?'}</>)
                            : (<>{'🔗'} {src.sourceApiEndpoint?.name || '?'} {'\u2192'} {src.sourceField?.name || '?'}</>)}
                        </span>
                      ) : (
                        <span className="text-gray-400 italic">Nicht konfiguriert</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {src ? (MODE_L[src.aggregationMode] || src.aggregationMode) : '\u2014'}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">
                      {src ? (TIMER_L[src.timer] || src.timer) : '\u2014'}
                    </td>

                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(f.id)} className="btn-ghost text-xs text-primary">
                        {src ? 'Bearbeiten' : 'Konfigurieren'}
                      </button>
                      {src && (
                        <button onClick={() => removeSource(src.id)} className="btn-ghost text-xs text-red-500 ml-2">
                          Loschen
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingField && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Datenquelle konfigurieren: {fieldMap.get(editingField)?.label || fieldMap.get(editingField)?.dataField?.name || 'Feld'}
              </h2>
              <button onClick={() => setEditingField(null)} className="btn-ghost p-1"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quell-Typ</label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="st" value="QUESTIONNAIRE" checked={form.sourceType === 'QUESTIONNAIRE'}
                      onChange={() => setForm({...form, sourceType: 'QUESTIONNAIRE'})} className="radio" />
                    <span className="text-sm">Fragebogen</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="st" value="API" checked={form.sourceType === 'API'}
                      onChange={() => setForm({...form, sourceType: 'API'})} className="radio" />
                    <span className="text-sm">API-Endpunkt</span>
                  </label>
                </div>
              </div>

              {form.sourceType === 'QUESTIONNAIRE' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quell-Fragebogen</label>
                    <select value={form.sourceQuestionnaireId}
                      onChange={(e) => setForm({...form, sourceQuestionnaireId: e.target.value, sourceFieldId: ''})}
                      className="input w-full">
                      <option value="">--- Fragebogen wählen ---</option>
                      {availQ.map((q: any) => <option key={q.id} value={q.id}>{q.name}</option>)}
                    </select>
                  </div>
                  {form.sourceQuestionnaireId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Datenfeld aus Quell-Fragebogen</label>
                      {(() => {
                        const selQ = availQ.find((q: any) => q.id === form.sourceQuestionnaireId);
                        const selQFields = selQ?.fields || [];
                        const filteredFields = selQFields.filter((sf: any) => {
                          const sfType = sf.dataField?.type || sf.type || '';
                          return !editingTargetType || sfType === editingTargetType;
                        });
                        return (
                          <select value={form.sourceFieldId}
                            onChange={(e) => setForm({...form, sourceFieldId: e.target.value})}
                            className="input w-full">
                            <option value="">--- Datenfeld wählen ---</option>
                            {filteredFields.map((sf: any) => {
                              const sfName = sf.dataField?.name || sf.name || '?';
                              const sfType = sf.dataField?.type || sf.type || '';
                              return (
                                <option key={sf.id} value={sf.dataField?.id || sf.id}>
                                  {sfName} ({sfType})
                                </option>
                              );
                            })}
                          </select>
                        );
                      })()}
                      {editingTargetType && (() => {
                        const selQ = availQ.find((q: any) => q.id === form.sourceQuestionnaireId);
                        const selQFields = selQ?.fields || [];
                        const filteredFields = selQFields.filter((sf: any) => {
                          const sfType = sf.dataField?.type || sf.type || '';
                          return sfType !== editingTargetType;
                        });
                        if (filteredFields.length > 0) return null;
                        const totalFields = selQFields.length;
                        if (totalFields > 0 && filteredFields.length === totalFields) {
                          return <p className="text-xs text-amber-600 mt-1">Keine Felder mit passendem Datentyp (erwartet: {editingTargetType})</p>;
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </>
              )}

              {form.sourceType === 'API' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">API-Endpunkt</label>
                    <select value={form.sourceApiEndpointId}
                      onChange={(e) => { setForm({...form, sourceApiEndpointId: e.target.value, apiFieldId: ''}); }}
                      className="input w-full">
                      <option value="">--- API wählen ---</option>
                      {availApis.map((a: any) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                  </div>
                  {form.sourceApiEndpointId && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Datenfeld (aus API-Mapping)</label>
                      <select value={form.apiFieldId}
                        onChange={(e) => setForm({...form, apiFieldId: e.target.value})}
                        className="input w-full">
                        <option value="">--- Datenfeld wählen ---</option>
                        {(availApis.find((a: any) => a.id === form.sourceApiEndpointId)?.fieldMappings || [])
                          .filter((m: any) => {
                            const sourceType = m.dataField?.type || '';
                            return !editingTargetType || sourceType === editingTargetType;
                          })
                          .map((m: any) => (
                            <option key={m.dataField.id} value={m.dataField.id}>
                              {m.dataField.name}{m.dataField.label ? ' (' + m.dataField.label + ')' : ''}{m.dataField.type ? ' [' + m.dataField.type + ']' : ''}
                            </option>
                          ))}
                        {(availApis.find((a: any) => a.id === form.sourceApiEndpointId)?.fieldMappings || [])
                          .filter((m: any) => {
                            const sourceType = m.dataField?.type || '';
                            return editingTargetType && sourceType !== editingTargetType;
                          }).length > 0 && (
                          <option value="" disabled className="text-gray-400 italic">--- Inkompatible Typen ausgeblendet ---</option>
                        )}
                      </select>
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aggregations-Modus</label>
                <select value={form.aggregationMode}
                  onChange={(e) => setForm({...form, aggregationMode: e.target.value})}
                  className="input w-full">
                  {Object.entries(MODE_L).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timer</label>
                <select value={form.timer}
                  onChange={(e) => setForm({...form, timer: e.target.value})}
                  className="input w-full">
                  {Object.entries(TIMER_L).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>



              {childOrgs.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium text-gray-700">Organisationen (optional)</label>
                    <button onClick={() => {
                      const allIds = childOrgs.map((o: any) => o.id).join(',');
                      setForm({...form, includedOrgIds: allIds});
                    }} className="text-xs text-primary">Alle auswählen</button>
                  </div>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {childOrgs.map((org: any) => {
                      const selectedIds = form.includedOrgIds ? form.includedOrgIds.split(',') : [];
                      const checked = selectedIds.includes(org.id);
                      return (
                        <label key={org.id} className="flex items-center gap-2 cursor-pointer text-sm"
                          style={{ paddingLeft: (org.level || 0) * 16 + 'px' }}>
                          <input type="checkbox" checked={checked}
                            onChange={() => {
                              const ids = new Set(form.includedOrgIds ? form.includedOrgIds.split(',') : []);
                              if (ids.has(org.id)) ids.delete(org.id); else ids.add(org.id);
                              setForm({...form, includedOrgIds: Array.from(ids).join(',')});
                            }}
                            className="checkbox" />
                          {org.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button onClick={() => setEditingField(null)} className="btn-secondary text-sm">Abbrechen</button>
                <button onClick={() => save(editingField)} disabled={pend}
                  className="btn-primary text-sm">
                  {pend ? 'Speichern...' : (getSource(editingField) ? 'Aktualisieren' : 'Speichern')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
